import styled, { css, keyframes } from "styled-components";
import Button from "../components/atoms/Buttons";

const glassPanel = css`
  border: 3px solid;
  border-color: #fff0b2 #b18236 #c99d4b #ebcb83;
  background: ${(props) => props.theme.raffleItemBackground || props.theme.itemBackground};
  box-shadow:
    inset 0 0 0 1px #7d4926,
    inset 0 6px 9px -3px #35000099,
    inset 4px 0 7px -3px #35000066,
    inset 0 -1px 0 #ffeabc99;
`;

export const ParticipantsLayout = styled.div`
  --coupon-width: clamp(90px, 8vw, 160px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 28px;
  height: calc(80vh - 2rem);
  padding: 28px 32px 32px;
  box-sizing: border-box;
  color: #fff8eb;
  font-family: 'Open Sans', Arial, sans-serif;
  & *, & *::before, & *::after { box-sizing: border-box; }
  @media (max-height: 600px) {
    height: calc(80vh - 1rem);
    padding: 16px 24px 20px;
    gap: 20px;
  }
  @media (max-width: 700px) {
    --coupon-width: 76px;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 150px minmax(0, 1fr);
    padding: 16px;
    gap: 16px;
  }
`;

export const StatsColumn = styled.div`
  display: grid;
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 24px;
  min-height: 0;
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: minmax(0, 1fr);
    gap: 12px;
  }
`;

export const StatCard = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  gap: clamp(10px, 1.5vh, 18px);
  padding: 20px 14px;
  ${glassPanel}
  border-radius: 18px;
  @media (max-width: 700px) { padding: 12px 8px; gap: 8px; }
`;

export const StatNumber = styled.p`
  margin: 0;
  font-size: clamp(44px, min(6.5vw, 11vh), 120px);
  line-height: 1;
  font-weight: 800;
  letter-spacing: -.04em;
  color: #ffe69a;
  text-shadow: 0 2px #be8829;
  @media (max-width: 700px) { font-size: 36px; }
`;

export const StatLabel = styled.h2`
  margin: 0;
  text-align: center;
  color: #ffe69a;
  font-size: clamp(16px, 1.7vw, 30px);
  line-height: 1.2;
  font-weight: 700;
  @media (max-width: 700px) { font-size: 12px; }
`;

export const PrivacyButton = styled(Button)`
  padding: 10px 18px;
  font-size: clamp(12px, 1.25vw, 22px);
  letter-spacing: 0;
  line-height: 1.2;
  @media (max-width: 700px) { padding: 6px 8px; font-size: 10px; }
`;

export const ParticipantTable = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  ${glassPanel}
  border-radius: 16px;
`;

export const ParticipantHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--coupon-width);
  flex-shrink: 0;
  align-items: center;
  min-height: 44px;
  border-bottom: 1px solid #d5ab4f;
  background: #00000014;
  color: #ffe69a;
  font-size: clamp(14px, 1.35vw, 24px);
  font-weight: 700;
  line-height: 1.2;
  & > span { padding: 10px 20px; }
  & > span:last-child { text-align: center; padding-inline: 4px; }
`;

const tickerScroll = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(calc(-100% * var(--ticker-rows) / var(--ticker-visible))); }
`;

// Viewport of the participant list. The track is as tall as the viewport and
// its rows overflow it, so translate percentages are multiples of the viewport.
export const TickerViewport = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const TickerTrack = styled.div`
  display: grid;
  grid-auto-rows: calc(100% / var(--ticker-visible));
  height: 100%;
  &[data-animated='true'] {
    will-change: transform;
    animation: ${tickerScroll} var(--ticker-duration) linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    &[data-animated='true'] { animation-duration: calc(var(--ticker-duration) * 3); }
  }
`;

export const ParticipantRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--coupon-width);
  width: 100%;
  height: 100%;
  border-bottom: 1px solid #c79a4a66;
  font-weight: 800;
  font-size: clamp(16px, min(2.1vw, 3.3vh), 38px);
  line-height: 1.1;
  text-transform: uppercase;
  & > span {
    display: flex;
    align-items: center;
    min-width: 0;
    min-height: 0;
    line-height: 1.1;
    padding: 4px 20px;
  }
  .participant-name { overflow-wrap: anywhere; text-align: left; }
  .participant-coupons {
    justify-content: center;
    padding-inline: 4px;
    color: #ffe69a;
    background: transparent;
    border-left: 1px solid #d5ab4f66;
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 700px) {
    font-size: 16px;
    & > span { padding: 4px 10px; }
  }
`;
